import React, { useContext, useEffect, useState } from "react";
import { StyledContainer, StyledHeader } from "../Notes/Notes.styles";
import { Grid } from "@mui/material";
import SimpleGrid, {
  CellProps,
  BodyProps
} from "components/shared/SimpleGrid/SimpleGrid";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "context/Provider";
import { NotesObject } from "context/initialState/Notes";
import API from "api";
import Session from "helpers/session";
import CustomizedDialogs, { CustomDialogProps } from "components/shared/DialogModal/DialogModal";
import { toLocalTime } from "helpers/variables";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import { StyledTextDetailsLabel} from "../../../../components/shared/DialogModal/DialogModel.styles";

interface AttributeProps {
    memberId: string;
}

export default function Notes({memberId}: AttributeProps) {
  const { notesDispatch, member } = useContext(GlobalContext);
  const { t } = useTranslation();
  const notesTableHeaders: CellProps[] = [
    { content: "Details", align: "left" },
    { content: t("u-note"), align: "left" }
  ];
  const [notesTableBody, setNotesTableBody] = useState();
  const [totalNotesList, setTotalNotesList] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<CustomDialogProps | null>(null);

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    API.kore.notes
      .getList(
        Session.crackCookieToken("coordinator"),
        `?sort_by=created_at&order=desc&filter=memberId:${
          window.location.pathname.split("/")[3]
        }&limit=${itemsPerPage}&page=${currentPage}`
      )
      .then((notesList) => {
        setNotesTableBody(notesList);
      });
  };

  const generateList = (
    rawList: NotesObject[],
    onRowClick: (props: CustomDialogProps) => void
  ): BodyProps[] => {
    return rawList.map((item) => ({
      cells: [
        {
          content: (
            <div>
              <StyledTextDetailsLabel>{item.authorName}</StyledTextDetailsLabel>
              <StyledTextDetailsLabel>{toLocalTime(item.createdAt)}</StyledTextDetailsLabel>
              <StyledTextDetailsLabel>
                {item.readingId ? "Reading note" : "Staff Note"}
              </StyledTextDetailsLabel>
            </div>
          ),
          align: "left"
        },
        {content: item.content, align: "left" }
      ],
      action: () =>
        onRowClick({
          coordinatorName: item.authorName,
          coordinatorId: item.authorId,
          patientName: `${member?.current.firstName} ${member?.current.lastName}`,
          patientId: item.memberId,
          readingId: item.readingId || "",
          date: toLocalTime(item.createdAt),
          content: item.content,
          isNote: false
        })
    }));
  };

  const handleRowClick = (rowProps: CustomDialogProps) => {
    setDialogProps(rowProps); // pass the data to the dialog
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogProps(null);
  };

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    API.kore.notes
      .getList(Session.crackCookieToken("coordinator"))
      .then((noteList) => {
        let i = 0;
        let filteredNotes = [];
        while (i < noteList.length) {
          if (noteList[i].memberId === window.location.pathname.split("/")[3]) {
            filteredNotes.push(noteList[i]);
          }
          i++;
        }
        notesDispatch({ type: "UPDATE_LIST", payload: filteredNotes });
        setTotalNotesList(filteredNotes.length);
        paginationAction(5, 0);
      });
  }, []);

  return (
    <StyledContainer>
      <StyledHeader>
        <h2>{t("u-staff-notes")}</h2>
      </StyledHeader>
      <EncounterTimerDisplay/>
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={10.5}>
          <Grid container>
            <Grid item xs={1} md={3} lg={5.5} />
            <Grid item xs={0.2} />
          </Grid>
          <SimpleGrid
            headers={notesTableHeaders}
            body={generateList(notesTableBody || [], handleRowClick)}
            paginationAction={paginationAction}
            totalList={totalNotesList}
          />
          {dialogProps && (
            <CustomizedDialogs
              {...dialogProps}
              open={dialogOpen}
              onClose={handleDialogClose}
            />
          )}
        </Grid>
      </Grid>
    </StyledContainer>
  );
}
